import React, { useState } from "react";
import Button from "@components/button";
import { WalletLoad } from "@apis/index";
import { toast } from "react-toastify";
import styled from "@emotion/styled";
import { Wallet, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import { Helper } from "@utils/index";
import request from "@utils/request";

interface WalletAdjustmentModalProps {
  user: any;
  onClose: () => void;
  onSuccess: () => void;
  currencySettings: any;
}

const WalletAdjustmentModal: React.FC<WalletAdjustmentModalProps> = ({
  user,
  onClose,
  onSuccess,
  currencySettings,
}) => {
  console.log({ user });
  const [amount, setAmount] = useState<string>("");
  const [type, setType] = useState<"credit" | "debit">("credit");
  const [note, setNote] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [currentBalance, setCurrentBalance] = useState<number>(user?.walletBalance || 0);

  React.useEffect(() => {
    const fetchBalance = async () => {
      try {
        const res = await request({
          url: `wallet-loads/admin-balance/${user.id}`,
          method: "get",
          authorization: true,
        });
        if (res && res.balance !== undefined) {
          setCurrentBalance(res.balance);
        }
      } catch (err) {
        console.error("Failed to fetch live balance:", err);
      }
    };
    fetchBalance();
  }, [user.id]);

  const handleSubmit = async () => {
    const numAmount = Number(amount);
    if (!numAmount || numAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setLoading(true);
    try {
      const data = {
        userId: user.id,
        amount: numAmount,
        adminNote: note,
      };

      if (type === "credit") {
        await WalletLoad.directCredit({ data });
      } else {
        // Simple check: debit shouldn't exceed balance unless forced
        if (numAmount > currentBalance) {
          if (
            !window.confirm(
              "Amount exceeds user's current balance. Do you want to continue?",
            )
          ) {
            setLoading(false);
            return;
          }
        }
        await WalletLoad.directDebit({ data });
      }

      toast.success(`Wallet ${type}ed successfully`);
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.message || "Failed to adjust wallet");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <div className="icon">
          <Wallet size={24} />
        </div>
        <div>
          <Title>Wallet Adjustment</Title>
          <Subtitle>
            Directly adjust balance for <strong>{user.storeName}</strong>
          </Subtitle>
        </div>
      </Header>

      <BalanceCard>
        <span className="label">Current Balance</span>
        <span className="value">
          {Helper.formatCurrency(currentBalance, currencySettings)}
        </span>
      </BalanceCard>

      <FormGroup>
        <label>Adjustment Type</label>
        <TypeToggle>
          <button
            type="button"
            className={type === "credit" ? "active credit" : ""}
            onClick={() => setType("credit")}
          >
            <ArrowUpCircle size={18} />
            <span>Add Funds (Credit)</span>
          </button>
          <button
            type="button"
            className={type === "debit" ? "active debit" : ""}
            onClick={() => setType("debit")}
          >
            <ArrowDownCircle size={18} />
            <span>Deduct Funds (Debit)</span>
          </button>
        </TypeToggle>
      </FormGroup>

      <FormGroup>
        <label>Amount ({currencySettings?.currencySymbol || "Rs."})</label>
        <Input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
          min="1"
        />
      </FormGroup>

      <FormGroup>
        <label>Admin Note</label>
        <TextArea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Reason for adjustment..."
        />
      </FormGroup>

      <Actions>
        <Button title="Cancel" bg="grey500" onClick={onClose} />
        <Button
          title={
            loading
              ? "Processing..."
              : `Confirm ${type.charAt(0).toUpperCase() + type.slice(1)}`
          }
          bg={type === "credit" ? "green500" : "red500"}
          onClick={handleSubmit}
          disabled={loading}
        />
      </Actions>
    </Container>
  );
};

const Container = styled.div`
  width: 450px;
  max-width: 90vw;
`;

const Header = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 24px;
  .icon {
    width: 48px;
    height: 48px;
    background: #eff6ff;
    color: #3b82f6;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0;
  color: #111827;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 4px 0 0;
`;

const BalanceCard = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border: 1px solid #e2e8f0;
  .label {
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
  }
  .value {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
  }
`;

const TypeToggle = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: white;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    transition: all 0.2s;

    &.active.credit {
      background: #ecfdf5;
      color: #059669;
      border-color: #059669;
    }
    &.active.debit {
      background: #fef2f2;
      color: #dc2626;
      border-color: #dc2626;
    }
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  outline: none;
  font-size: 14px;
  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  outline: none;
  font-size: 14px;
  resize: none;
  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

export default WalletAdjustmentModal;
